<template>
  <div>
    <card v-if="status !== 'COMPUTING'">
      <div class="container no-preview">
        <button class="slds-button slds-button_neutral" @click.once="computePreview" :disabled="isView">
          {{ $t('campaignDesigner.finalCheckAndCampaignRelease.computePreview') }}
        </button>
      </div>
    </card>
    <card v-if="['COMPUTING', 'COMPUTED', 'ERROR'].includes(status)" :has-empty-title="status === 'COMPUTED' && !!previewData && !!Object.keys(this.previewData).length">
      <button
        :disabled="loading"
        @click="download"
        class="slds-button slds-button_neutral slds-button_brand"
        slot="actions"
        v-if="status === 'COMPUTED' && previewData && Object.keys(this.previewData).length">

        {{$t('action.downloadAsCsv')}}
      </button>
      <spinner v-if="loading" />
      <template v-else>
        <div class="container" v-if="status === 'COMPUTING'">
          <span class="slds-text-heading_medium">
            {{ $t('campaignDesigner.finalCheckAndCampaignRelease.computingPreview') }}
          </span>
          <spinner class="spinner"/>
        </div>
        <div v-else-if="status === 'ERROR'">
          <section class="slds-modal_prompt slds-fade-in-open">
            <div class="slds-modal__container">
              <header class="slds-modal__header slds-theme_error slds-theme_alert-texture">
                <h2 class="slds-text-heading_medium" id="prompt-heading-id">{{$t('campaignDesigner.finalCheckAndCampaignRelease.previewError')}}</h2>
              </header>
              <div class="slds-modal__content slds-p-around_medium" id="prompt-message-wrapper">
                <p>{{previewError.message}}</p>
              </div>
            </div>
          </section>
        </div>
        <div v-else-if="status === 'COMPUTED'" style="display: flex; flex-direction: row;">
          <template v-if="previewData && previewData.length">
            <!-- Charts -->
            <div style="flex-grow: 1">
              <card additional-classes="slds-card_boundary slds-m-right--x-large" v-for="(chart, index) in charts" :key="`t_${index}`">
                <g-chart
                  type="PieChart"
                  :data="chart.data"
                  :options="chart.options"
                  :resizeDebounce="500" />
              </card>
            </div>
            <!-- Table -->
            <div style="flex-grow: 1">
              <table class="slds-table slds-table_cell-buffer slds-table_bordered">
                <thead>
                  <tr class="slds-line-height_reset">
                    <th class="" scope="col" v-if="targetSource === 'contacts_leads'">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.leadType.label')}}</div>
                    </th>
                    <th class="" scope="col" v-if="targetSource === 'contacts_leads'">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.brand')}}</div>
                    </th>
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.channel')}}</div>
                    </th>
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.contactCount')}}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in previewData" :key="index">
                    <td v-if="targetSource === 'contacts_leads'">
                      <div class="slds-truncate" v-if="targetSource === 'contacts_leads'">{{$t(`campaignDesigner.finalCheckAndCampaignRelease.leadType.${row.lead_type}`)}}</div>
                    </td>
                    <td v-if="targetSource === 'contacts_leads'">
                      <div class="slds-truncate" v-if="targetSource === 'contacts_leads'">{{$t(`brands.${row.brand}`)}}</div>
                    </td>
                    <td>
                      <div class="slds-truncate">{{row.availableChannels}}</div>
                    </td>
                    <td>
                      <div class="slds-truncate">{{row.contactCount}}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
          <span class="slds-text-heading_medium" v-else>
            {{ $t('campaignDesigner.finalCheckAndCampaignRelease.emptyPreview') }}
          </span>
        </div>
      </template>
    </Card>
  </div>
</template>
<script>
import _ from 'lodash'
import { exportAsCsv } from '../../utils/fileUtils'
import { getErrorMessage } from '../../utils/utils'
import HasMode from '../../utils/HasMode'
import Card from '../../slds/Card'
import Spinner from '../../../components/Spinner'
export default {
  mixins: [HasMode],
  components: {
    Card,
    Spinner
  },
  props: {
    loading: Boolean,
    mode: String
  },
  computed: {
    status () {
      if (this.previewData && this.jobId && this.computedPreview && Object.keys(this.previewData).length >= 0) {
        return 'COMPUTED'
      }
      if (this.jobId && this.previewError) {
        return 'ERROR'
      }
      if (this.jobId) {
        return 'COMPUTING'
      }
      return 'MISSING'
    }
  },
  data () {
    return {
      campaignId: '',
      channelsTable: [],
      charts: [],
      computedPreview: false,
      jobId: '',
      refreshJobInterval: null,
      previewData: {},
      previewError: undefined,
      showChannelsTable: false,
      targetSource: ''
    }
  },
  async activated () {
    this.$emit('startLoading')
    await this.callLoadFromStore()
    this.$emit('endLoading')
  },
  methods: {
    mapByField (data, field, labelPrefix) {
      const contactsByField = data.reduce((acc, val) => {
        let key
        if (labelPrefix) {
          key = this.$t(`${labelPrefix}.${val[field]}`)
        } else {
          key = val[field]
        }
        if (!acc[val[field]]) {
          acc[key] = parseInt(val.contactCount)
        } else {
          acc[key] += parseInt(val.contactCount)
        }

        return acc
      }, {})

      return Object.entries(contactsByField).map(([k, v]) => [k, v])
    },
   async callLoadFromStore () {
      const campaign = this.$store.getters['campaignDesigner/state'].currentCampaign
      this.targetSource = campaign.config.campaignData.targetSource.selected
      this.campaignId = campaign.id
      this.jobId = campaign.config.preview && campaign.config.preview.jobId ? campaign.config.preview.jobId : null

      if (this.jobId) {
        await this.poll()
      }
    },
    async computePreview () {
      try {
        if (this.status === 'COMPUTED') {
          this.$store.dispatch('campaignDesigner/clearPreview')
          await this.callLoadFromStore()
          this.previewData = {}
          this.jobId = null
          this.computedPreview = false
        }
        let functionToCall
        if (this.isCreate) {
          functionToCall = 'save'
        } else {
          functionToCall = 'update'
        }
        this.$emit('callFunction', { functionToCall, callback: this.startComputePreview })
      } catch (error) {
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    download () {
      if (this.targetSource === 'contacts_leads') {
        const mappedPreview = this.previewData
          .map(row => {
            return [
              this.$t(`campaignDesigner.finalCheckAndCampaignRelease.leadType.${row.lead_type}`),
              this.$t(`brands.${row.brand}`),
              row.availableChannels,
              row.contactCount,
            ]
          }
        )

        exportAsCsv([[
          this.$t('campaignDesigner.finalCheckAndCampaignRelease.leadType.label'),
          this.$t('campaignDesigner.finalCheckAndCampaignRelease.brand'),
          this.$t('campaignDesigner.finalCheckAndCampaignRelease.channel'),
          this.$t('campaignDesigner.finalCheckAndCampaignRelease.contactCount')
        ],
        ...mappedPreview], 'Target_Preview.csv')
      } else {
        const mappedPreview = this.previewData
          .map(row => {
            return [
              row.availableChannels,
              row.contactCount
            ]
          }
        )

        exportAsCsv([[
          this.$t('campaignDesigner.finalCheckAndCampaignRelease.channel'),
          this.$t('campaignDesigner.finalCheckAndCampaignRelease.contactCount')
        ],
        ...mappedPreview], 'Target_Preview.csv')
      }


    },
    getSectionToSave () {
      return {
        config: {
          jobId: this.jobId
        }
      }
    },
    async poll () {
      const response = await this.$store.dispatch('campaignDesigner/checkPreview', { jobId: this.jobId })
      if (response.status === 'Completed') {
        const preview = await this.$store.dispatch('campaignDesigner/getPreview', { campaignId: response.detail.campaignId.toString() })
        this.previewData = _.sortBy(preview.preview, ['brand', 'targetCluster', 'availableChannels'])
        this.computedPreview = true
      } else if (response.status === 'Error') {
        this.previewError = response.statusDetail
      }
    },
    async startComputePreview (campaignId) {
      const startPreviewBody = {
        campaignId: this.campaignId || campaignId,
        department: 'SALES',
        recordType: 'salesFull'
      }

      const response = await this.$store.dispatch('campaignDesigner/startPreview', startPreviewBody)
      this.jobId = response.id
    },
    async startPreviewPolling () {
      this.refreshJobInterval = setInterval(this.poll, 5000)
    },
    validate () {
      return true
    }
  },
  watch: {
    previewData (data) {
      if (data) {
        const options = {
          colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
          pieSliceText: 'label',
          width: 500,
          height: 500,
        }

        if (this.targetSource === 'contacts_leads') {
          this.charts = [
            {
              data: [
                ['Lead Type', 'Count'],
                ...this.mapByField(data, 'lead_type', 'campaignDesigner.finalCheckAndCampaignRelease.leadType')
              ],
              options
            },
            {
              data: [
                ['Brand', 'Count'],
                ...this.mapByField(data, 'brand', 'brands')
              ],
              options
            },
            {
              data: [
                ['Brand', 'Count'],
                ...this.mapByField(data, 'availableChannels')
              ],
              options
            }
          ]
        } else {
          this.charts = [
            {
              data: [
                ['Brand', 'Count'],
                ...this.mapByField(data, 'availableChannels')
              ],
              options
            }
          ]
        }
      }
    },
    status (newValue) {
      if (newValue === 'COMPUTING') {
        this.startPreviewPolling()
      } else if (newValue === 'COMPUTED') {
        if (this.refreshJobInterval) {
          clearInterval(this.refreshJobInterval)
        }
      }
    }
  },
  beforeDestroy () {
    if (this.refreshJobInterval) {
      clearInterval(this.refreshJobInterval)
    }
  }
}
</script>
<style lang="scss" scoped>
  .chart-container {
    margin-bottom: 10px;
  }
  .container {
    display: flex;
    justify-content: center;
  }
  .spinner {
    margin-top: 20px;
  }
</style>
